import { Address } from "viem";
import { Set } from "../models/Set";

export enum xLotteryState {
  Open,
  Mixing,
  Drawing,
}

export type xTicket = {
  mainNumbers: readonly bigint[];
  luckyStars: readonly bigint[];
};

export type xSet = {
  mainNumbers: bigint; // [0, main_number_pool_number]
  luckyStars: bigint; // [0, lucky_star_pool_number]
};

function _normalize(val: unknown): bigint {
  if (typeof val === "bigint") return val;
  if (typeof val === "number") return BigInt(val);
  if (typeof val === "string") {
    // Remove whitespace and handle numeric strings
    const trimmed = val.trim();
    if (/^\d+$/.test(trimmed)) return BigInt(trimmed);
    // Try to parse as float and convert to bigint if possible
    const num = Number(trimmed);
    if (!isNaN(num)) return BigInt(num);
  }
  throw new Error("Unsupported value type for comparison: " + val);
}

// Set or xSet may contain strings, numbers or bigints,
// so we need to normalize them to bigints before comparison.
export function combinationEquals(a: xSet | Set, b: xSet | Set): boolean {
  try {
    return (
      _normalize(a.mainNumbers) === _normalize(b.mainNumbers) &&
      _normalize(a.luckyStars) === _normalize(b.luckyStars)
    );
  } catch {
    return false;
  }
}

export type xTierAllocations = {
  tier: xSet;
  cut: bigint; // Percentage [0, 1] ETH (100% = 1 ETHER)
};

export type xTierWinners = {
  tier: xSet;
  winners: bigint;
};

export type xProceeds = {
  prizes: bigint;
  donations: bigint;
  royalties: bigint;
};

export type xLotteryConfig = {
  pools: xSet; // main_number_pool_number and lucky_star_pool_number (50, 12)
  jackpotTier: xSet; // main_number_match and lucky_star_match (5, 2)
  tierAllocations: readonly xTierAllocations[];
  proceedsAllocation: xProceeds;
  ticketPrice: bigint;
  ticketTotalLimit: bigint;
  ticketBuyLimit: bigint;
};

export type xUpkeepConfig = {
  period: bigint;
  triggers: readonly bigint[];
};

export type xPlayer = {
  unclaimedPrizes: bigint;
  lastDrawUpdate: bigint;
  points: bigint;
};

export type xNode = {
  active: boolean;
  index: bigint;
  previous: Address;
  next: Address;
};

export type xCharityInfo = {
  name: string;
  website: string;
};

export type xCharity = {
  info: xCharityInfo;
  unclaimedDonations: bigint;
  node: xNode;
};

export type xVRFConfig = {
  callbackGasLimit: bigint;
  requestConfirmations: bigint;
};

export type xLotteryInfo = {
  nextLotteryConfig: xLotteryConfig;
  upkeepConfig: xUpkeepConfig;
  vrfConfig: xVRFConfig;
  lotteryState: xLotteryState;
  lastUpkeep: bigint;
  drawNumber: bigint;
  lotteryConfig: xLotteryConfig;
  jackpot: bigint;
  charityAddress: Address;
  proceeds: xProceeds;
};

export type xUserInfo = {
  player: xPlayer;
  lastDrawPrize: bigint;
  currentTickets: readonly xTicket[];
  charity: xCharity;
  isOwner: boolean;
};

///
/// Events
///

export type xLotteryStateChangedEvent = {
  drawNumber: bigint;
  fromState: xLotteryState;
  toState: xLotteryState;
};

export type xPlayerEntered = {
  drawNumber: bigint;
  playerAddress: Address;
  newPlayerPoints: bigint;
};

export type xTicketsBoughtEvent = {
  drawNumber: bigint;
  playerAddress: Address;
  tickets: xTicket[];
};

export type PlayerReferredEvent = {
  drawNumber: bigint;
  playerAddress: Address;
  playerReferralAddress: Address;
  pointsGiven: bigint;
  newPlayerReferralPoints: bigint;
};

export type DiscountAppliedEvent = {
  drawNumber: bigint;
  playerAddress: Address;
  newPlayerPoints: bigint;
};

// export type xDonationClaimedEvent = {
//   charityAddress: Address;
//   donations: bigint;
//   timestamp: bigint;
// };

// export type xRoyaltiesClaimedEvent = {
//   ownerAddress: Address;
//   royalties: bigint;
//   timestamp: bigint;
// };

export type xDrawOpenedEvent = {
  drawNumber: bigint;
  charityAddress: Address;
  jackpot: bigint;
  config: xLotteryConfig;
};

export type xDrawUpdatedEvent = {
  drawNumber: bigint;
  proceeds: xProceeds;
};

export type xDrawPostponedEvent = {
  drawNumber: bigint;
};

export type xDrawClosedEvent = {
  drawNumber: bigint;
  ticket: xTicket;
  tierWinners: readonly xTierWinners[];
};

export type xCharityAddedEvent = {
  charityAddress: Address;
  charityInfo: xCharityInfo;
};

export type xCharityInfoUpdatedEvent = {
  charityAddress: Address;
  charityInfo: xCharityInfo;
};

export type xPlayerReferredEvent = {
  drawNumber: bigint;
  playerAddress: Address;
  referrerAddress: Address;
  points: bigint;
};

// export type xCharityRemovedEvent = {
//   charityAddress: Address;
//   charity: xCharity;
//   timestamp: bigint;
// };

export type xUpkeepConfigSetEvent = {
  upkeepConfig: xUpkeepConfig;
};

export type xUpkeepPerformedEvent = {
  lastUpkeep: bigint;
};

// ************** Indexer **************** //
export type IndexerMainNumberStatistics = {
  __typename: "MainNumberStatistics";
  mainNumber: bigint;
  lastDrawNumber: bigint;
  count: bigint;
};

export type IndexerLuckyStarStatistics = {
  __typename: "LuckyStarStatistics";
  luckyStar: bigint;
  lastDrawNumber: bigint;
  count: bigint;
};

export type IndexerLotteryStatistics = {
  __typename: "LotteryStatistics";
  proceeds_donations: bigint;
  proceeds_prizes: bigint;
  playersEntered: bigint;
  jackpotsWon: bigint;
  highestPrizeWon: bigint;
  highestJackpot: bigint;
  ticketsBought: bigint;
  winningTickets: bigint;
  prizesWon: bigint;
};

export type IndexerCharity = {
  __typename: "Charity";
  charityAddress: string;
  donationsReceived: bigint;
  drawsInvolved: bigint;
  highestDonationReceived: bigint;
  name: string;
  website: string;
};

export type IndexerPlayer = {
  __typename: "Player";
  playerAddress: string;
  ticketsBought: bigint;
  prizesWon: bigint;
  drawsEntered: bigint;
  winningTickets: bigint;
  jackpotsWon: bigint;
  highestPrizeWon: bigint;
};

export type IndexerTicket = {
  __typename: "Ticket";
  luckyStars: bigint[];
  mainNumbers: bigint[];
};

export type IndexerEntry = {
  __typename: "Entry";
  ticketsBought: bigint;
  winningTickets: bigint;
  tickets: IndexerTicket[];
  playerAddress: string;
};

export type IndexerTierAllocation = {
  __typename: "TierAllocation";
  tier_luckyStar: bigint;
  tier_mainNumber: bigint;
  cut: bigint;
};

export type IndexerTierResult = {
  __typename: "TierResult";
  index: bigint;
  prize: bigint;
  prizePerWinner: bigint;
  tier_luckyStar: bigint;
  tier_mainNumber: bigint;
  winningTickets: bigint;
};

export type IndexerDraw = {
  __typename: "Draws";
  charity: IndexerCharity | undefined;
  drawConfig_jackpotTier_luckyStars: bigint;
  drawConfig_jackpotTier_mainNumbers: bigint;
  drawConfig_pools_luckyStars: bigint;
  drawConfig_pools_mainNumbers: bigint;
  drawConfig_proceedsAllocation_donations: bigint;
  drawConfig_proceedsAllocation_prizes: bigint;
  drawConfig_ticketBuyLimit: bigint;
  drawConfig_proceedsAllocation_royalties: bigint;
  drawConfig_ticketPrice: bigint;
  drawConfig_ticketTotalLimit: bigint;
  drawConfig_tierAllocations: IndexerTierAllocation[];
  drawNumber: bigint;
  entries: IndexerEntry[];
  jackpot: bigint;
  playersEntered: bigint;
  prizesWon: bigint;
  proceeds_donations: bigint;
  proceeds_prizes: bigint;
  proceeds_royalties: bigint;
  ticket_luckyStars: bigint[];
  ticket_mainNumbers: bigint[];
  ticketsBought: bigint;
  tiersResults: IndexerTierResult[];
  timestamp: bigint;
  winningTickets: bigint;
};

export type StatisticsQuery = {
  player: IndexerPlayer | undefined;
  mainNumberStatistics_collection: IndexerMainNumberStatistics[];
  luckyStarStatistics_collection: IndexerLuckyStarStatistics[];
  lotteries: IndexerLotteryStatistics[];
  draws: IndexerDraw[];
  charities: IndexerCharity[];
};

export type StatisticsQueryVariables = Record<string, never>;
